import { IterateDataOutput, IterateStep } from '@/types/automationSteps.ts';
import { Group, Stack } from '@mantine/core';
import * as React from 'react';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import SingleChildDataInputSelector from '@/pages/Editor/components/SharedFields/DataInputSelector/SingleChildDataInputSelector.tsx';
import { DEFAULT_DYNAMIC_DATA_INPUT, STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { DatasetType } from '@/types/automationFields.ts';
import StepFieldsRemoveButton from '@/pages/Editor/components/StepFieldsRemoveButton.tsx';
import OutputPinButton from '@/pages/Editor/components/StepSidebar/OutputPinButton.tsx';

type props = {
  iterateDataOutput: IterateDataOutput;
  onChangeNoDelay: (updatedInput: IterateDataOutput) => void;
  onChangeDelay: (updatedInput: IterateDataOutput) => void;
  onDelete: () => void;
  step: IterateStep;
  inputIndex: number;
};

const DATASET_OUTPUT_OPTIONS = [
  { label: 'Json', value: DatasetType.Json },
  { label: 'List', value: DatasetType.Dataframe },
  { label: 'File', value: DatasetType.File },
  { label: 'Directory', value: DatasetType.Directory },
];

const IterateDataOutputFields = ({
  iterateDataOutput,
  onChangeNoDelay,
  onChangeDelay,
  onDelete,
  step,
  inputIndex,
}: props) => {
  const invalidChildOutput = useStepSpecificError(
    step,
    StepErrorType.InvalidChildOutput,
    inputIndex,
  );

  const isNewStep = useIsNewStep(step.step_id);

  return (
    <Stack
      style={{ borderColor: '#ECECED', borderWidth: 1, borderRadius: 5 }}
      bg={'#F6F6F6'}
      p={'xs'}
      gap={'xs'}
    >
      <Stack>
        <Group gap={'xs'} wrap={'nowrap'} align={'start'}>
          <StyledTextInput
            label="Name"
            value={iterateDataOutput.name}
            placeholder="Output Name"
            onChange={(event) => {
              const value = event.currentTarget.value.replace(/[/\n]/g, '');
              onChangeDelay({ ...iterateDataOutput, name: value });
            }}
            required={true}
            maxLength={STEP_FIELD_MAX_LENGTH}
            w={'100%'}
            error={!iterateDataOutput.name && !isNewStep ? 'Name cannot be empty' : undefined}
          />
          <OutputPinButton step={step} outputId={iterateDataOutput.output_id} />
          <StepFieldsRemoveButton onDelete={onDelete} />
        </Group>
      </Stack>

      <SingleChildDataInputSelector
        step={step}
        label={'Child Output'}
        dataInput={iterateDataOutput.child_data_output}
        onChange={(dataInput) =>
          onChangeNoDelay({
            ...iterateDataOutput,
            child_data_output: dataInput || DEFAULT_DYNAMIC_DATA_INPUT,
          })
        }
        dataInputError={invalidChildOutput}
        required={true}
      />

      <Stack>
        <StyledSelect
          label="Data Output Type"
          placeholder="Select Output Data Type"
          data={DATASET_OUTPUT_OPTIONS}
          value={iterateDataOutput.output_type}
          onChange={(value) =>
            onChangeNoDelay({ ...iterateDataOutput, output_type: value as DatasetType })
          }
          required={true}
          allowDeselect={false}
          clearable={false}
        />
      </Stack>
    </Stack>
  );
};

export default IterateDataOutputFields;
